/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   buildTiles
 */

import { normalizeLng } from '@zcode/nr-utils'
import type { BoundsObj } from '@zcode/state-type'

interface Box {
  bounds: BoundsObj
}
// comparator function to sort bounds objects
const sortBounds = (lat: number, lng: number, b1: Box, b2: Box) => {
  if (b1 === b2) {
    return 0
  }
  const d1 =
    (b1?.bounds?.minLat - lat) * (b1?.bounds?.minLat - lat) +
    (b1?.bounds?.minLng - lng) * (b1?.bounds?.minLng - lng)
  const d2 =
    (b2?.bounds?.minLat - lat) * (b2?.bounds?.minLat - lat) +
    (b2?.bounds?.minLng - lng) * (b2?.bounds?.minLng - lng)

  if (d1 < d2) {
    return -1
  }
  return d1 > d2 ? 1 : 0
}

interface mapConfig {
  maxLat: number
  maxLng: number
  minLat: number
  minLng: number
  zoom: number
}

export const buildTiles = (config: mapConfig) => {
  const { maxLat, maxLng, minLat, minLng, zoom } = config
  // increment the request batch counter
  // calculate the degrees lat/lng per tile. the lat/lng Step
  // calculate the tile coordinates (the 'index' vars),
  // and the total number of tiles per 360 degrees of lng (lngSteps), use this to handle edge
  // condition of +-180 meridian.
  //  (lngSteps is derived via round() b/c it should be an int, but floating point math could be
  //  off by a tiny amount, round gives us the correct nearest int vs floor/ceil which could go
  //  the wrong way)
  //
  // constants are derived as 360 sliced by powers of 2 ^ raised to the zoom
  // at zoom 4: we want 4 boxes vertially / 4 horizontal.
  // This gives us reasonably sized tile boxes, the desire is to have about 4 tiles per view of a
  // laptop type sized screen at any zoom, and values that are derived from 360 to not produce
  // overlap at 0 or +-180 meridians (or the poles)
  const center = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 }
  const scale = Math.pow(0.5, zoom - 3)
  const latStep = 45.0 * scale
  const lngStep = 90.0 * scale
  const lngSteps = Math.round(360.0 / lngStep)
  const minLngIdx = Math.floor(minLng / lngStep)
  const minLatIdx = Math.floor(minLat / latStep)
  const maxLatIdx = Math.floor(maxLat / latStep)

  let maxLngIdx = Math.floor(maxLng / lngStep)
  const tiles = []

  // if spanning the 180/-180 line, eg. min=179, max=-177, with say tile indices = (15, 1)
  // then we want to add the total steps (say 16 in this eg) to the max, to get a tile range of
  // (15, 17), thus we'd generate 3 horizontal tiles. below. we just want max > min to make the
  // foorloop logic simple.
  maxLngIdx += maxLngIdx < minLngIdx ? lngSteps : 0

  for (let i = minLatIdx; i <= maxLatIdx; ++i) {
    let jmod
    for (let j = minLngIdx; j <= maxLngIdx; ++j) {
      // 'mod' j by lngSteps, e.g convert back from 17 (mod 16) to 1. subtraction is faster
      // then multiply by the lngStep (degrees per tile), and be sure to normalize that into
      // +-180, as it could produce a value eg. 190, which should become -170
      jmod = j > lngSteps ? j - lngSteps : j
      tiles.push({
        bounds: {
          minLat: i * latStep,
          maxLat: (i + 1) * latStep,
          minLng: normalizeLng(jmod * lngStep),
          maxLng: normalizeLng((jmod + 1) * lngStep),
        },
      })
    }
  }
  // request more visually central tiles first
  tiles.sort((t1, t2) => sortBounds(center.lat, center.lng, t1, t2))
  return tiles
}
